import { useRef, useState } from 'react'
import {
  ETAPAS_XABCDE,
  type AchadoXabcde,
  type CenarioExtracao,
  type EtapaXabcde,
  type VeiculoCenarioExtracao,
  type VitimaCenarioExtracao,
} from '../dominio/didatico/cenariosExtracao'
import {
  CONFIGURACOES_VEICULO,
  DECUBITOS,
  NOMES_CONFIGURACAO,
  NOMES_DECUBITO,
  NOMES_SENTIDO_CABECA,
} from '../dominio/didatico/configuracaoVeiculo'
import { IDS_PERFIL_VITIMA, PERFIS_VITIMA } from '../dominio/didatico/perfisVitima'
import {
  cenarioProntoParaAlunos,
  novaVitimaAutoral,
  novoVeiculoAutoral,
  referenciaVazia,
  validarCenarioAutoral,
  type CenarioAutoral,
  type ReferenciaCorrecaoVitima,
} from '../dominio/didatico/cenariosAutorais'
import type { ControleCenariosAutorais } from './useCenariosAutorais'

const ANCORAS = [
  { id: 'motorista', nome: 'Motorista' },
  { id: 'passageiro', nome: 'Passageiro dianteiro' },
  { id: 'traseiroEsquerdo', nome: 'Traseiro esquerdo' },
  { id: 'traseiroCentral', nome: 'Traseiro central' },
  { id: 'traseiroDireito', nome: 'Traseiro direito' },
] as const

const REGIOES: ReadonlyArray<{ id: NonNullable<AchadoXabcde['regiao']>; nome: string }> = [
  { id: 'cabeca', nome: 'Cabeça' },
  { id: 'torax', nome: 'Tórax' },
  { id: 'abdome', nome: 'Abdome' },
  { id: 'membroInferior', nome: 'Membro inferior' },
  { id: 'corpoTodo', nome: 'Corpo todo' },
]

const paraLinhas = (texto: string): readonly string[] =>
  texto.split('\n').map((linha) => linha.trim()).filter(Boolean)

function comCenario(autoral: CenarioAutoral, alteracao: Partial<CenarioExtracao>): CenarioAutoral {
  return { ...autoral, cenario: { ...autoral.cenario, ...alteracao } }
}

function comVeiculo(autoral: CenarioAutoral, veiculoId: string, alteracao: Partial<VeiculoCenarioExtracao>): CenarioAutoral {
  return comCenario(autoral, {
    veiculos: autoral.cenario.veiculos.map((v) => (v.id === veiculoId ? { ...v, ...alteracao } : v)),
  })
}

function comVitima(autoral: CenarioAutoral, vitimaId: string, alteracao: Partial<VitimaCenarioExtracao>): CenarioAutoral {
  return comCenario(autoral, {
    vitimas: autoral.cenario.vitimas.map((v) => (v.id === vitimaId ? { ...v, ...alteracao } : v)),
  })
}

function comAchado(autoral: CenarioAutoral, vitimaId: string, etapa: EtapaXabcde, alteracao: Partial<AchadoXabcde>): CenarioAutoral {
  const vitima = autoral.cenario.vitimas.find((v) => v.id === vitimaId)
  if (!vitima) return autoral
  return comVitima(autoral, vitimaId, { xabcde: { ...vitima.xabcde, [etapa]: { ...vitima.xabcde[etapa], ...alteracao, informado: true } } })
}

function comReferencia(autoral: CenarioAutoral, vitimaId: string, alteracao: Partial<ReferenciaCorrecaoVitima>): CenarioAutoral {
  const existe = autoral.referencia.some((r) => r.vitimaId === vitimaId)
  const referencia = existe
    ? autoral.referencia.map((r) => (r.vitimaId === vitimaId ? { ...r, ...alteracao } : r))
    : [...autoral.referencia, { ...referenciaVazia(vitimaId), ...alteracao }]
  return { ...autoral, referencia }
}

interface Props {
  autorais: ControleCenariosAutorais
  cenariosCatalogo: readonly CenarioExtracao[]
  aoPreVisualizarComoAluno: (cenarioId: string) => void
}

/**
 * Autoria de cenário (Fase 5B): "preparar aula" do professor. Edita
 * diretamente o `CenarioAutoral` selecionado — cada campo chama
 * `autorais.atualizar` na hora, sem estado espelhado local. A referência de
 * correção (`referencia`) nunca aparece fora deste painel e da exportação
 * "professor"; a exportação "aluno" e a pré-visualização usam sempre
 * `cenario`, nunca `referencia`.
 */
export function PainelAutoriaCenario({ autorais, cenariosCatalogo, aoPreVisualizarComoAluno }: Props) {
  const [idSelecionado, setIdSelecionado] = useState<string | null>(autorais.lista[0]?.id ?? null)
  const [idBaseEscolhida, setIdBaseEscolhida] = useState('')
  const [mensagemImportacao, setMensagemImportacao] = useState<{ sucesso: boolean; mensagem: string } | null>(null)
  const entradaArquivo = useRef<HTMLInputElement>(null)

  const autoral = autorais.lista.find((item) => item.id === idSelecionado) ?? null
  const atualizar = (proximo: CenarioAutoral) => autorais.atualizar(proximo)

  const baixarArquivo = (conteudo: string, nomeArquivo: string) => {
    const url = URL.createObjectURL(new Blob([conteudo], { type: 'application/json' }))
    const link = document.createElement('a')
    link.href = url
    link.download = nomeArquivo
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.setTimeout(() => URL.revokeObjectURL(url), 0)
  }

  const nomeArquivo = (nome: string, sufixo: string) => {
    const slug = nome.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase()
    return `${slug || 'cenario'}-${sufixo}.json`
  }

  const aoSelecionarArquivo = async (evento: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = evento.target.files?.[0]
    evento.target.value = ''
    if (!arquivo) return
    if (arquivo.size > 5 * 1024 * 1024) {
      setMensagemImportacao({ sucesso: false, mensagem: 'Arquivo maior que 5 MB.' })
      return
    }
    const texto = await arquivo.text()
    const resultado = autorais.importar(texto)
    setMensagemImportacao(resultado)
  }

  return (
    <div className="grupo-controle painel-autoria-cenario">
      <span className="titulo-controle">Autoria de cenário — preparar aula</span>
      <p className="dica">
        Escreva o cenário para os alunos investigarem: condições iniciais, achados por avaliação e a referência de
        correção — visível só aqui, nunca no que o aluno vê.
      </p>

      <div className="lista-cenarios-autorais">
        {autorais.lista.map((item) => (
          <button
            key={item.id}
            className={item.id === idSelecionado ? 'ativo' : ''}
            onClick={() => setIdSelecionado(item.id)}
          >
            {item.cenario.nome || '(sem nome)'}
          </button>
        ))}
      </div>

      <div className="linha-botoes">
        <button onClick={() => setIdSelecionado(autorais.criar())}>Novo cenário vazio</button>
        <label className="campo campo-inline">
          <select value={idBaseEscolhida} onChange={(e) => setIdBaseEscolhida(e.target.value)}>
            <option value="">Partir de um cenário existente…</option>
            {cenariosCatalogo.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
          <button
            disabled={!idBaseEscolhida}
            onClick={() => {
              const base = cenariosCatalogo.find((c) => c.id === idBaseEscolhida)
              if (!base) return
              setIdSelecionado(autorais.criarAPartirDe(base))
              setIdBaseEscolhida('')
            }}
          >
            Criar cópia
          </button>
        </label>
      </div>

      {autoral && (
        <EditorCenarioAutoral
          key={autoral.id}
          autoral={autoral}
          atualizar={atualizar}
          onRemover={() => {
            autorais.remover(autoral.id)
            setIdSelecionado(null)
          }}
          onDuplicar={() => setIdSelecionado(autorais.duplicar(autoral.id))}
          onPreVisualizar={() => aoPreVisualizarComoAluno(autoral.id)}
          onExportarProfessor={() => {
            const json = autorais.exportarProfessor(autoral.id)
            if (json) baixarArquivo(json, nomeArquivo(autoral.cenario.nome, 'professor'))
          }}
          onExportarAluno={() => {
            const json = autorais.exportarAluno(autoral.id)
            if (json) baixarArquivo(json, nomeArquivo(autoral.cenario.nome, 'aluno'))
          }}
        />
      )}

      <div className="linha-botoes">
        <button onClick={() => entradaArquivo.current?.click()}>Importar cenário (JSON do professor)</button>
        <input
          ref={entradaArquivo}
          type="file"
          accept="application/json,.json"
          style={{ display: 'none' }}
          onChange={aoSelecionarArquivo}
        />
      </div>
      {mensagemImportacao && (
        <p role="status" className={`mensagem-importacao ${mensagemImportacao.sucesso ? 'sucesso' : 'erro'}`}>
          {mensagemImportacao.mensagem}
        </p>
      )}
    </div>
  )
}

function EditorCenarioAutoral({
  autoral,
  atualizar,
  onRemover,
  onDuplicar,
  onPreVisualizar,
  onExportarProfessor,
  onExportarAluno,
}: {
  autoral: CenarioAutoral
  atualizar: (proximo: CenarioAutoral) => void
  onRemover: () => void
  onDuplicar: () => void
  onPreVisualizar: () => void
  onExportarProfessor: () => void
  onExportarAluno: () => void
}) {
  const alertas = validarCenarioAutoral(autoral)
  const pronto = cenarioProntoParaAlunos(autoral)

  const adicionarVeiculo = () => {
    if (autoral.cenario.veiculos.length >= 2) return
    const id = `${autoral.id}-veiculo-${autoral.cenario.veiculos.length + 1}`
    atualizar(comCenario(autoral, { veiculos: [...autoral.cenario.veiculos, novoVeiculoAutoral(id)] }))
  }

  const removerVeiculo = (veiculoId: string) => {
    if (autoral.cenario.vitimas.some((v) => v.veiculoId === veiculoId)) return
    atualizar(comCenario(autoral, { veiculos: autoral.cenario.veiculos.filter((v) => v.id !== veiculoId) }))
  }

  const adicionarVitima = () => {
    if (autoral.cenario.vitimas.length >= 2) return
    const veiculoId = autoral.cenario.veiculos[0]?.id
    if (!veiculoId) return
    const id = `${autoral.id}-vitima-${autoral.cenario.vitimas.length + 1}`
    atualizar({
      ...comCenario(autoral, { vitimas: [...autoral.cenario.vitimas, novaVitimaAutoral(id, veiculoId)] }),
      referencia: [...autoral.referencia, referenciaVazia(id)],
    })
  }

  const removerVitima = (vitimaId: string) => {
    atualizar({
      ...comCenario(autoral, { vitimas: autoral.cenario.vitimas.filter((v) => v.id !== vitimaId) }),
      referencia: autoral.referencia.filter((r) => r.vitimaId !== vitimaId),
    })
  }

  return (
    <div className="editor-cenario-autoral">
      <label className="campo">
        <span>Nome do cenário</span>
        <input value={autoral.cenario.nome} onChange={(e) => atualizar(comCenario(autoral, { nome: e.target.value }))} />
      </label>
      <label className="campo">
        <span>Resumo (o que o professor vê na lista)</span>
        <textarea value={autoral.cenario.resumo} onChange={(e) => atualizar(comCenario(autoral, { resumo: e.target.value }))} />
      </label>
      <label className="campo">
        <span>Objetivo pedagógico</span>
        <textarea value={autoral.cenario.objetivo} onChange={(e) => atualizar(comCenario(autoral, { objetivo: e.target.value }))} />
      </label>
      <label className="campo">
        <span>Pausas didáticas (uma por linha)</span>
        <textarea
          value={autoral.cenario.pausas.join('\n')}
          onChange={(e) => atualizar(comCenario(autoral, { pausas: paraLinhas(e.target.value) }))}
        />
      </label>

      <details open>
        <summary>Protocolo de triagem declarado</summary>
        <p className="dica">
          Só metadado — nome, versão, população e fonte. O app não calcula categoria nem cor a partir disso; a
          classificação continua sendo escrita pelo professor na referência de correção, abaixo.
        </p>
        <label className="campo">
          <span>Nome do protocolo</span>
          <input value={autoral.protocolo.nome} onChange={(e) => atualizar({ ...autoral, protocolo: { ...autoral.protocolo, nome: e.target.value } })} />
        </label>
        <label className="campo">
          <span>Versão</span>
          <input value={autoral.protocolo.versao} onChange={(e) => atualizar({ ...autoral, protocolo: { ...autoral.protocolo, versao: e.target.value } })} />
        </label>
        <label className="campo">
          <span>População de aplicação</span>
          <input value={autoral.protocolo.populacao} onChange={(e) => atualizar({ ...autoral, protocolo: { ...autoral.protocolo, populacao: e.target.value } })} />
        </label>
        <label className="campo">
          <span>Fonte</span>
          <input value={autoral.protocolo.fonte} onChange={(e) => atualizar({ ...autoral, protocolo: { ...autoral.protocolo, fonte: e.target.value } })} />
        </label>
      </details>

      {autoral.cenario.veiculos.map((veiculo) => (
        <details key={veiculo.id}>
          <summary>Veículo: {veiculo.nome || veiculo.id}</summary>
          <EditorVeiculo
            veiculo={veiculo}
            onAlterar={(alteracao) => atualizar(comVeiculo(autoral, veiculo.id, alteracao))}
            onRemover={autoral.cenario.veiculos.length > 1 ? () => removerVeiculo(veiculo.id) : undefined}
            bloqueadoPorVitima={autoral.cenario.vitimas.some((v) => v.veiculoId === veiculo.id)}
          />
        </details>
      ))}
      {autoral.cenario.veiculos.length < 2 && <button onClick={adicionarVeiculo}>Adicionar veículo</button>}

      {autoral.cenario.vitimas.map((vitima) => (
        <details key={vitima.id} open>
          <summary>Vítima: {vitima.nome || vitima.id}</summary>
          <EditorVitima
            vitima={vitima}
            veiculos={autoral.cenario.veiculos}
            referencia={autoral.referencia.find((r) => r.vitimaId === vitima.id) ?? referenciaVazia(vitima.id)}
            onAlterarVitima={(alteracao) => atualizar(comVitima(autoral, vitima.id, alteracao))}
            onAlterarAchado={(etapa, alteracao) => atualizar(comAchado(autoral, vitima.id, etapa, alteracao))}
            onAlterarReferencia={(alteracao) => atualizar(comReferencia(autoral, vitima.id, alteracao))}
            onRemover={autoral.cenario.vitimas.length > 1 ? () => removerVitima(vitima.id) : undefined}
          />
        </details>
      ))}
      {autoral.cenario.vitimas.length < 2 && <button onClick={adicionarVitima}>Adicionar vítima</button>}

      {alertas.length > 0 && (
        <ul className="alertas-validacao-autoral">
          {alertas.map((alerta, indice) => (
            <li key={indice} className={alerta.nivel}>{alerta.mensagem}</li>
          ))}
        </ul>
      )}
      {pronto && <p className="destaque-decisao">Sem pendências — pronto para uso em aula.</p>}

      <div className="linha-botoes">
        <button onClick={onPreVisualizar}>Pré-visualizar como aluno</button>
        <button onClick={onExportarProfessor}>Exportar completo (professor)</button>
        <button onClick={onExportarAluno}>Exportar sem gabarito (aluno)</button>
        <button onClick={onDuplicar}>Duplicar</button>
        <button onClick={onRemover}>Excluir cenário</button>
      </div>
    </div>
  )
}

function EditorVeiculo({
  veiculo,
  onAlterar,
  onRemover,
  bloqueadoPorVitima,
}: {
  veiculo: VeiculoCenarioExtracao
  onAlterar: (alteracao: Partial<VeiculoCenarioExtracao>) => void
  onRemover?: () => void
  bloqueadoPorVitima: boolean
}) {
  return (
    <>
      <label className="campo">
        <span>Nome</span>
        <input value={veiculo.nome} onChange={(e) => onAlterar({ nome: e.target.value })} />
      </label>
      <label className="campo">
        <span>Configuração</span>
        <select value={veiculo.configuracao} onChange={(e) => onAlterar({ configuracao: e.target.value as VeiculoCenarioExtracao['configuracao'] })}>
          {CONFIGURACOES_VEICULO.map((c) => <option key={c} value={c}>{NOMES_CONFIGURACAO[c]}</option>)}
        </select>
      </label>
      {veiculo.configuracao !== 'sobreRodas' && (
        <>
          <label className="campo">
            <span>Decúbito</span>
            <select value={veiculo.decubito ?? 'dorsal'} onChange={(e) => onAlterar({ decubito: e.target.value as VeiculoCenarioExtracao['decubito'] })}>
              {DECUBITOS.map((d) => <option key={d} value={d}>{NOMES_DECUBITO[d]}</option>)}
            </select>
          </label>
          <label className="campo">
            <span>Sentido da cabeça</span>
            <select value={veiculo.sentidoCabeca ?? 'frente'} onChange={(e) => onAlterar({ sentidoCabeca: e.target.value as VeiculoCenarioExtracao['sentidoCabeca'] })}>
              <option value="frente">{NOMES_SENTIDO_CABECA.frente}</option>
              <option value="traseira">{NOMES_SENTIDO_CABECA.traseira}</option>
            </select>
          </label>
        </>
      )}
      <label className="toggle">
        <input type="checkbox" checked={veiculo.estabilidadeInicial} onChange={(e) => onAlterar({ estabilidadeInicial: e.target.checked })} />
        Estável desde o início
      </label>
      <label className="campo">
        <span>Riscos iniciais (um por linha)</span>
        <textarea value={veiculo.riscosIniciais.join('\n')} onChange={(e) => onAlterar({ riscosIniciais: paraLinhas(e.target.value) })} />
      </label>
      <label className="campo">
        <span>Acesso inicial</span>
        <select value={veiculo.acessoInicial} onChange={(e) => onAlterar({ acessoInicial: e.target.value as VeiculoCenarioExtracao['acessoInicial'] })}>
          <option value="livre">Livre</option>
          <option value="limitado">Limitado</option>
          <option value="bloqueado">Bloqueado</option>
        </select>
      </label>
      {onRemover && (
        <button onClick={onRemover} disabled={bloqueadoPorVitima} title={bloqueadoPorVitima ? 'Uma vítima ainda usa este veículo.' : undefined}>
          Remover veículo
        </button>
      )}
    </>
  )
}

function EditorVitima({
  vitima,
  veiculos,
  referencia,
  onAlterarVitima,
  onAlterarAchado,
  onAlterarReferencia,
  onRemover,
}: {
  vitima: VitimaCenarioExtracao
  veiculos: readonly VeiculoCenarioExtracao[]
  referencia: ReferenciaCorrecaoVitima
  onAlterarVitima: (alteracao: Partial<VitimaCenarioExtracao>) => void
  onAlterarAchado: (etapa: EtapaXabcde, alteracao: Partial<AchadoXabcde>) => void
  onAlterarReferencia: (alteracao: Partial<ReferenciaCorrecaoVitima>) => void
  onRemover?: () => void
}) {
  return (
    <>
      <label className="campo">
        <span>Nome</span>
        <input value={vitima.nome} onChange={(e) => onAlterarVitima({ nome: e.target.value })} />
      </label>
      <label className="campo">
        <span>Veículo</span>
        <select value={vitima.veiculoId} onChange={(e) => onAlterarVitima({ veiculoId: e.target.value })}>
          {veiculos.map((v) => <option key={v.id} value={v.id}>{v.nome || v.id}</option>)}
        </select>
      </label>
      <label className="campo">
        <span>Posição (âncora)</span>
        <select value={vitima.idAncora} onChange={(e) => onAlterarVitima({ idAncora: e.target.value })}>
          {ANCORAS.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}
        </select>
      </label>
      <label className="campo">
        <span>Perfil corporal</span>
        <select value={vitima.idPerfil} onChange={(e) => onAlterarVitima({ idPerfil: e.target.value })}>
          {IDS_PERFIL_VITIMA.map((id) => <option key={id} value={id}>{PERFIS_VITIMA[id].nome}</option>)}
        </select>
      </label>
      <label className="campo">
        <span>Perceptível na chegada (um por linha) — o que o aluno vê antes de qualquer avaliação</span>
        <textarea value={vitima.perceptivel.join('\n')} onChange={(e) => onAlterarVitima({ perceptivel: paraLinhas(e.target.value) })} />
      </label>
      <label className="campo">
        <span>Estado clínico (geometria/cinemática)</span>
        <select value={vitima.estadoClinico} onChange={(e) => onAlterarVitima({ estadoClinico: e.target.value as VitimaCenarioExtracao['estadoClinico'] })}>
          <option value="estavel">Estável</option>
          <option value="grave">Grave</option>
        </select>
      </label>
      <label className="campo">
        <span>Urgência (catálogo/referência do professor)</span>
        <select value={vitima.urgencia} onChange={(e) => onAlterarVitima({ urgencia: e.target.value as VitimaCenarioExtracao['urgencia'] })}>
          <option value="avaliar">A avaliar</option>
          <option value="estavel">Estável</option>
          <option value="maior">Maior</option>
        </select>
      </label>
      <label className="campo">
        <span>Encarceramento</span>
        <select value={vitima.encarceramento} onChange={(e) => onAlterarVitima({ encarceramento: e.target.value as VitimaCenarioExtracao['encarceramento'] })}>
          <option value="mecanico">Mecânico</option>
          <option value="fisicoI">Físico tipo I</option>
          <option value="fisicoII">Físico tipo II</option>
        </select>
      </label>
      <label className="campo">
        <span>Leitura do encarceramento</span>
        <textarea value={vitima.leituraEncarceramento} onChange={(e) => onAlterarVitima({ leituraEncarceramento: e.target.value })} />
      </label>
      <label className="campo">
        <span>Aprisionamentos — um por linha, formato "tipo: descrição" (tipos: painel, pedais, colunaB, bancoCinto, deformacao)</span>
        <textarea
          value={vitima.aprisionamentos.map((a) => `${a.tipo}: ${a.descricao}`).join('\n')}
          onChange={(e) => {
            const tiposValidos = new Set(['painel', 'pedais', 'colunaB', 'bancoCinto', 'deformacao'])
            const aprisionamentos = paraLinhas(e.target.value).flatMap((linha) => {
              const [tipo, ...resto] = linha.split(':')
              const tipoLimpo = tipo?.trim() ?? ''
              return tiposValidos.has(tipoLimpo)
                ? [{ tipo: tipoLimpo as VitimaCenarioExtracao['aprisionamentos'][number]['tipo'], descricao: resto.join(':').trim() }]
                : []
            })
            onAlterarVitima({ aprisionamentos })
          }}
        />
      </label>

      <details>
        <summary>Achados por avaliação (XABCDE)</summary>
        {ETAPAS_XABCDE.map((etapa) => (
          <div key={etapa} className="editor-achado-xabcde">
            <strong>{etapa}</strong>
            <label className="campo">
              <span>Resumo (curto, aparece no rótulo 3D)</span>
              <input value={vitima.xabcde[etapa].resumo} onChange={(e) => onAlterarAchado(etapa, { resumo: e.target.value })} />
            </label>
            <label className="campo">
              <span>Detalhe (texto completo do painel)</span>
              <textarea value={vitima.xabcde[etapa].detalhe} onChange={(e) => onAlterarAchado(etapa, { detalhe: e.target.value })} />
            </label>
            <label className="campo">
              <span>Região destacada em 3D</span>
              <select
                value={vitima.xabcde[etapa].regiao ?? ''}
                onChange={(e) => onAlterarAchado(etapa, { regiao: (e.target.value || undefined) as AchadoXabcde['regiao'] })}
              >
                <option value="">Nenhuma</option>
                {REGIOES.map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
              </select>
            </label>
            {vitima.xabcde[etapa].informado === false && <span className="dica">Ainda não preenchido — o aluno verá "Não informado".</span>}
          </div>
        ))}
      </details>

      <details>
        <summary>Referência de correção (só o professor vê)</summary>
        <p className="dica">
          Texto livre do professor — o app não calcula certo/errado a partir disso. O debriefing compara com o que o
          aluno encontrou/declarou, sem veredito automático.
        </p>
        <fieldset>
          <legend>Achados essenciais (para comparar com o que o aluno encontrou)</legend>
          {ETAPAS_XABCDE.map((etapa) => (
            <label key={etapa} className="toggle">
              <input
                type="checkbox"
                checked={referencia.achadosEssenciais.includes(etapa)}
                onChange={(e) => {
                  const achadosEssenciais = e.target.checked
                    ? [...referencia.achadosEssenciais, etapa]
                    : referencia.achadosEssenciais.filter((item) => item !== etapa)
                  onAlterarReferencia({ achadosEssenciais })
                }}
              />
              {etapa}
            </label>
          ))}
        </fieldset>
        <label className="campo">
          <span>Classificação esperada</span>
          <textarea value={referencia.classificacaoEsperada} onChange={(e) => onAlterarReferencia({ classificacaoEsperada: e.target.value })} />
        </label>
        <label className="campo">
          <span>Alternativas aceitas (uma por linha)</span>
          <textarea value={referencia.alternativasAceitas.join('\n')} onChange={(e) => onAlterarReferencia({ alternativasAceitas: paraLinhas(e.target.value) })} />
        </label>
        <label className="campo">
          <span>Prioridades de atenção</span>
          <textarea value={referencia.prioridadesAtencao} onChange={(e) => onAlterarReferencia({ prioridadesAtencao: e.target.value })} />
        </label>
        <label className="campo">
          <span>Riscos de atraso</span>
          <textarea value={referencia.riscosDeAtraso} onChange={(e) => onAlterarReferencia({ riscosDeAtraso: e.target.value })} />
        </label>
        <label className="campo">
          <span>Restrições geométricas</span>
          <textarea value={referencia.restricoesGeometricas} onChange={(e) => onAlterarReferencia({ restricoesGeometricas: e.target.value })} />
        </label>
        <label className="campo">
          <span>Pontos que dependem de julgamento do instrutor</span>
          <textarea value={referencia.pontosDeJulgamento} onChange={(e) => onAlterarReferencia({ pontosDeJulgamento: e.target.value })} />
        </label>
      </details>

      {onRemover && <button onClick={onRemover}>Remover vítima</button>}
    </>
  )
}
